import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { track } from '../lib/track';
import '../styles/ServiceEnquiry.css';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ f, value, onChange, formId }) => {
  const common = { id: `${formId}-${f.name}`, name: f.name, value, onChange, required: f.required, 'data-testid': `${formId}-${f.name}` };
  return (
    <label className="service-enquiry-field" htmlFor={common.id}>
      <span>{f.label}{f.required && <span className="service-enquiry-req"> *</span>}</span>
      {f.type === 'select' ? (
        <select {...common}>
          <option value="">Please choose…</option>
          {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : f.type === 'textarea' ? (
        <textarea {...common} rows={4} minLength={f.minLength} maxLength={f.maxLength || 3000} placeholder={f.placeholder} />
      ) : (
        <input {...common} type={f.type || 'text'} maxLength={f.maxLength || 200} placeholder={f.placeholder} autoComplete={f.autoComplete} />
      )}
    </label>
  );
};

/**
 * Config-driven qualified enquiry form: validation, consent notice, success state,
 * form_start/form_submit tracking (no personal data in events).
 */
export const QualifiedEnquiryForm = ({ id, endpoint, title, intro, fields, submitLabel, successTitle, successBody, onSuccess, successAction }) => {
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.name, ''])));
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (name) => (e) => setForm((s) => ({ ...s, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = fields.find((f) => f.required && !String(form[f.name]).trim());
    if (missing) { setError(`Please complete “${missing.label}”.`); return; }
    const short = fields.find((f) => f.minLength && String(form[f.name]).trim().length < f.minLength);
    if (short) { setError(`“${short.label}” needs at least ${short.minLength} characters.`); return; }
    if (!consent) { setError('Please confirm the consent notice so we can respond.'); return; }
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, String(v).trim() || null]));
      await axios.post(`${API}${endpoint}`, { ...payload, consent: true });
      track('form_submit', id);
      setDone(true);
      onSuccess?.(payload);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? 'Please check the highlighted details and try again.' : (d || 'Something went wrong. Please try again or email hello@n3uro.world.'));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="service-enquiry-form service-enquiry-done" data-testid={`${id}-success`}>
        <CheckCircle2 size={28} color="#5BE9FF" aria-hidden="true" />
        <h3>{successTitle}</h3>
        <p>{successBody}</p>
        {successAction}
      </div>
    );
  }

  return (
    <form className="service-enquiry-form" onSubmit={submit} noValidate data-track-form={id} data-testid={`${id}-form`}>
      {title && <h3 className="service-enquiry-headline">{title}</h3>}
      {intro && <p className="service-enquiry-note" style={{ textAlign: 'left' }}>{intro}</p>}
      <div className="service-enquiry-grid">
        {fields.map((f) => (
          <div key={f.name} className={f.wide ? 'service-enquiry-wide' : ''}>
            <Field f={f} formId={id} value={form[f.name]} onChange={set(f.name)} />
          </div>
        ))}
      </div>
      <label className="qf-consent" data-testid={`${id}-consent`}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid={`${id}-consent-checkbox`} />
        <span>
          I agree that N3URO WORLD® may store these details and contact me about this enquiry. We don&rsquo;t sell data
          or add you to marketing lists. See our <Link to="/privacy-policy">Privacy Policy</Link>.
        </span>
      </label>
      {error && <p className="service-enquiry-error" role="alert" data-testid={`${id}-error`}>{error}</p>}
      <button type="submit" className="service-enquiry-submit" disabled={busy} data-testid={`${id}-submit`}>
        <span>{busy ? 'Sending…' : submitLabel}</span> <ArrowRight size={16} strokeWidth={2.4} />
      </button>
    </form>
  );
};

export default QualifiedEnquiryForm;
